import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type NavigationGuardNext,
  type RouteLocationNormalized,
} from "vue-router";
import HomeView from "@/views/HomeView.vue";
import middlewarePipeline from "@/router/middlewarePipeline";
import requireAuth from "@/router/middleware/requireAuth";
import requireAdmin from "@/router/middleware/requireAdmin";
import { useAuthStore } from "@/stores/auth";
import requireOnBoarding from "@/router/middleware/requireOnBoarding";
import { useCompanyStore } from "@/stores/company";
import onlySurvey from "@/router/middleware/onlySurvey";
import { useUserStore } from "@/stores/user";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: {
      layout: "DefaultLayout",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/LoginView.vue"),
    meta: {
      layout: "Auth",
    },
  },
  {
    path: "/welcome",
    name: "welcome",
    component: () => import("../views/WelcomeView.vue"),
    meta: {
      layout: "Setup",
      middleware: [requireAuth, onlySurvey],
    },
  },
  {
    path: "/config",
    name: "config",
    component: () => import("../views/SettingView.vue"),
    meta: {
      layout: "Setup",
      middleware: [requireAuth, onlySurvey],
    },
  },
  {
    path: "/company-information",
    name: "company-information",
    component: () => import("../views/CompanyInformationView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireAdmin, onlySurvey],
    },
  },
  {
    path: "/emissions-config",
    name: "emissions-config",
    component: () => import("../views/EmissionMatrixView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireAdmin, onlySurvey],
    },
  },
  {
    path: "/user-information",
    name: "user-information",
    component: () => import("../views/UserInformationView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, onlySurvey],
    },
  },
  {
    path: "/setting",
    name: "setting",
    component: () => import("../views/SettingView.vue"),
    meta: {
      layout: "Setup",
      status: 20,
      middleware: [requireAuth, onlySurvey],
    },
    children: [
      {
        path: "/setting/personal",
        name: "personal-setting",
        component: () => import("../components/setting/PersonalSetting.vue"),
        meta: {
          layout: "Setup",
          status: 20,
          back: null,
          middleware: [requireAuth, onlySurvey],
        },
      },
      {
        path: "/setting/company",
        name: "company-setting",
        component: () => import("../components/setting/CompanySetting.vue"),
        meta: {
          layout: "Setup",
          status: 40,
          back: "personal-setting",
          middleware: [requireAuth, requireAdmin, onlySurvey],
        },
      },
      {
        path: "/setting/operation-info",
        name: "operation-info",
        component: () => import("../components/setting/OperationInfo.vue"),
        meta: {
          layout: "Setup",
          status: 40,
          back: "company-setting",
          middleware: [requireAuth, requireAdmin, onlySurvey],
        },
      },
      {
        path: "/setting/facility",
        name: "facility-setting",
        component: () => import("../components/setting/FacilitySetting.vue"),
        meta: {
          layout: "Setup",
          status: 60,
          back: "company-setting",
          middleware: [requireAuth, requireAdmin, onlySurvey],
        },
      },
      {
        path: "/setting/facilities",
        name: "facility-list",
        component: () => import("../components/setting/FacilityOnboardingList.vue"),
        meta: {
          layout: "Setup",
          status: 80,
          back: "facility-setting",
          middleware: [requireAuth, requireAdmin, onlySurvey],
        },
      },
      {
        path: "/setting/emission-setting",
        name: "facility-emission-sources",
        // Onboarding hosts the emission-matrix flow (no year selector; writes to all
        // unlocked years; confirming completes onboarding).
        component: () => import("../views/EmissionMatrixView.vue"),
        props: { mode: "onboarding" },
        meta: {
          layout: "Setup",
          status: 90,
          back: "facility-list",
          middleware: [requireAuth, requireAdmin, onlySurvey],
        },
      },
    ],
  },
  {
    path: "/finish",
    name: "setting-finish",
    component: () => import("../views/SettingFinishView.vue"),
    meta: { layout: "Setup", middleware: [requireAuth, onlySurvey] },
  },
  {
    path: "/calculator",
    name: "calculator",
    component: () => import("../views/CalculatorView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    // Skills: the catalogue, a Skill's workspace, and the builder behind it. One wildcard record —
    // the screen owns its own routing under this base, so an instance keeps its transcript and its
    // open pane while the URL moves.
    path: "/skills/:rest*",
    name: "skills",
    component: () => import("../views/SkillsView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    // "Structure your data": a file lands, the columns are read and reviewed, the rows go into a
    // record. Standalone — it belongs to no Skill (D186), which is why it is its own route.
    path: "/structure-data",
    name: "structure-data",
    component: () => import("../views/StructureDataView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/views",
    name: "views",
    component: () => import("../views/ViewsListView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/views/builder",
    name: "views-builder",
    component: () => import("../views/ViewBuilderView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/views/:id",
    name: "view-page",
    component: () => import("../views/ViewPageView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/analysis",
    name: "analysis",
    component: () => import("../views/AnalysisView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/goals",
    name: "goal",
    component: () => import("../views/TargetView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/reports",
    name: "reports",
    component: () => import("../views/ReportsView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/ghg-report/:reportId",
    name: "ghg-report",
    component: () => import("../views/GhgReport.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/reports/settings",
    name: "reports-settings",
    component: () => import("../views/SettingView.vue"),
    meta: {
      layout: "Setup",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
    children: [
      {
        path: "/reports/settings/report-type",
        name: "report-type",
        component: () =>
          import("../components/reports/ReportTypeSelection.vue"),
        meta: {
          layout: "Setup",
          middleware: [requireAuth, requireOnBoarding, onlySurvey],
          status: 33,
          back: "reports"
        },
      },
      {
        path: "reports/settings/internal-report-config",
        name: "internal-report-config",
        component: () =>
          import("../components/reports/InternalReportConfig.vue"),
        meta: {
          layout: "Setup",
          middleware: [requireAuth, requireOnBoarding, onlySurvey],
          status: 66,
          back: "report-type" 
        },
      },
      {
        path: "reports/settings/internal-report-config/preview",
        name: "internal-report-preview",
        component: () => import("../components/reports/InternalReportPreview.vue"),
        meta: {
          layout: "Setup",
          middleware: [requireAuth, requireOnBoarding, onlySurvey],
          status: 100,
          back: "internal-report-config"  
        },
      },
    ],
  },
  {
    path: "/comparator",
    name: "comparator",
    component: () => import("../views/CalculatorView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/survey",
    name: "survey",
    component: () => import("../views/SurveyView.vue"),
    meta: { layout: "Default", middleware: [requireAuth] },
  },
  {
    path: "/survey/:id",
    name: "survey_detail",
    component: () => import("../views/SurveyIdView.vue"),
    meta: { layout: "Default", middleware: [requireAuth] },
  },
  {
    path: "/decarbonization",
    name: "decarbonization",
    component: () => import("../views/DecarbonizationView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireAdmin, requireOnBoarding, onlySurvey],
    },
  },
  {
    // The file transformer: the organisation's transformations, and one run. The run id is a path
    // segment so a run keeps its URL and somebody can send it to a colleague.
    path: "/transformer/:runId?",
    name: "transformer",
    component: () => import("../views/structure/StructureHost.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding],
    },
  },
  {
    path: "/data-entry",
    name: "data-entry",
    component: () => import("../views/DataEntryView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  // Deep link routes for individual forms
  {
    path: "/forms/:formId",
    name: "form-direct",
    component: () => import("../views/DataEntryView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  // Form Builder routes
  {
    path: "/form-builder",
    name: "form-builder",
    component: () => import("../views/FormBuilderView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/form-builder/new",
    name: "form-builder-new",
    component: () => import("../views/FormBuilderView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/form-builder/:id",
    name: "form-builder-edit",
    component: () => import("../views/FormBuilderView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    path: "/forms/:formId/draft/:draftId",
    name: "form-draft-direct",
    component: () => import("../views/DataEntryView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  // API deeplink route - catch API URLs and redirect to form
  {
    path: "/api/input-forms/:formId",
    name: "api-form-redirect",
    component: () => import("../views/DataEntryView.vue"),
    meta: {
      layout: "Default",
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
    },
  },
  {
    // One wildcard record: studio owns everything under /data-explorer (catalog, saved views,
    // report mode) and the screen is told where it is instead of being remounted per URL.
    path: '/data-explorer/:rest*',
    name: 'data-explorer',
    component: () => import('../views/DataExplorerHost.vue'),
    meta: {
      layout: 'Default',
      middleware: [requireAuth, requireOnBoarding, onlySurvey],
      fillsViewport: true,
    }
  },
  {
    path: '/data-report/:rest*',
    name: 'data-report',
    component: () => import('../views/DataReportHost.vue'),
    meta: {
      layout: 'Default',
      middleware: [requireAuth],
      fillsViewport: true,
    }
  },
  {
    path: '/entity-definition',
    name: 'entity-definition',
    component: () => import('../views/EntityDefinition.vue'),
    meta: {
      layout: 'Default',
      middleware: [requireAuth]
    }
  },
  {
    path: '/rule-engine/:rest*',
    name: 'rule-engine',
    component: () => import('../views/AutomationsHost.vue'),
    meta: {
      layout: 'Default',
      middleware: [requireAuth],
      fillsViewport: true,
    }
  },
  {
    path: '/activity-log',
    name: 'activity-log',
    //@ts-ignore
    component: () => import('../views/ActivityLog.vue'),
    meta: {
      layout: 'Default',
      middleware: [requireAuth, requireOnBoarding, onlySurvey]
    }
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(
  (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const authStore = useAuthStore();
    const companyStore = useCompanyStore();
    const user = useUserStore();
    if (!to.meta.middleware) {
      return next();
    }

    const middleware = to.meta.middleware as any;

    const context = {
      to,
      from,
      next,
      authStore,
      companyStore,
      user
    };

    return middleware[0]({
      ...context,
      next: middlewarePipeline(context, middleware, 1),
    });
  }
);

export default router;
